import { Link, useNavigate } from "react-router-dom";
import { Boxes, Zap, Sparkles, FlaskConical, Swords, TrendingUp, Map, ScanSearch, Camera } from "lucide-react";
import { Layout } from "../Layout";

const NAV = [
  { key: "roadmap", label: "Founder Success Roadmap", to: "/ai-resources/roadmap", icon: Map },
  { key: "opportunity-scanner", label: "AI Opportunity Scanner", to: "/ai-resources/opportunity-scanner", icon: Camera, badge: "NEW" },
  { key: "name-checker", label: "Startup Name Checker", to: "/ai-resources/name-checker", icon: ScanSearch },
  { key: "tool-stack", label: "AI Tool Stack Audit", to: "/ai-resources/tool-stack", icon: Boxes },
  { key: "workflow", label: "AI Workflow", to: "/ai-resources/workflow", icon: Zap },
  { key: "validation-lab", label: "Validation Lab", to: "/ai-resources/validation-lab", icon: FlaskConical },
  { key: "competitor-search", label: "Competitor Search", to: "/ai-resources/competitor-search", icon: Swords },
  { key: "market-size", label: "Market Size Calculator", to: "/ai-resources/market-size", icon: TrendingUp },
  { key: "tool-recommendations", label: "Tool Recommendations", to: "/ai-resources/tool-recommendations", icon: Sparkles },
];

export const ToolBadge = ({ name }) => (
  <span
    title={name}
    className="w-7 h-7 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-[11px] font-bold text-[#374151] shadow-sm"
    data-testid="tool-badge"
  >
    {(name || "?").trim().charAt(0).toUpperCase()}
  </span>
);

export const StepLabel = ({ n }) => (
  <p className="text-[11px] font-bold uppercase tracking-widest text-[#D4AF37]">Step {n}</p>
);

export const DiffPill = ({ level }) => {
  const map = { Easy: "bg-green-50 text-green-600", Medium: "bg-amber-50 text-amber-600", Hard: "bg-red-50 text-red-500" };
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${map[level] || map.Medium}`}>{level}</span>;
};

export const AIResourcesShell = ({ active, title, subtitle, icon: Icon = Zap, extra = null, actions = null, children }) => {
  const navigate = useNavigate();
  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
        {/* Sidebar */}
        <aside className="space-y-5">
          <p className="text-[11px] font-bold uppercase tracking-widest text-[#9aa1ab]">AI Resources</p>
          <nav className="flex flex-col gap-1">
            {NAV.map((n) => {
              const on = active === n.key;
              return (
                <button
                  key={n.key}
                  onClick={() => navigate(n.to)}
                  data-testid={`airesources-nav-${n.key}`}
                  className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-semibold text-left transition-colors ${
                    on ? "bg-[#FBF7EA] text-[#8a6d1a] border border-[#D4AF37]/40" : "text-[#687280] hover:bg-gray-50 border border-transparent"
                  }`}
                >
                  <n.icon size={17} className={on ? "text-[#D4AF37]" : "text-[#9aa1ab]"} /> <span className="flex-1">{n.label}</span>
                  {n.badge && <span className="text-[9px] font-extrabold bg-[#D4AF37] text-[#0F0F0F] px-1.5 py-0.5 rounded-full">{n.badge}</span>}
                </button>
              );
            })}
          </nav>
          {extra}
        </aside>

        {/* Main */}
        <div className="min-w-0">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2">
                <span className="w-9 h-9 rounded-xl bg-[#0F0F0F] text-[#D4AF37] flex items-center justify-center"><Icon size={18} /></span>
                <h1 className="font-display text-3xl font-extrabold text-[#0F0F0F]">{title}</h1>
              </div>
              {subtitle && <p className="text-[#687280] mt-2">{subtitle}</p>}
            </div>
            {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
          </div>
          {children}
        </div>
      </div>
    </Layout>
  );
};

export const SidebarPromo = ({ title, desc, cta, onCta }) => (
  <div className="rounded-2xl bg-gradient-to-br from-[#0F0F0F] to-[#23200f] text-white p-5">
    <Sparkles size={20} className="text-[#D4AF37]" />
    <p className="font-display text-lg font-bold mt-2">{title}</p>
    <p className="text-sm text-[#c9c39f] mt-1 leading-snug">{desc}</p>
    {cta && (
      <button onClick={onCta} className="mt-4 w-full bg-[#D4AF37] text-[#0F0F0F] rounded-full py-2.5 text-sm font-bold hover:bg-[#c5a030] transition-colors" data-testid="sidebar-promo-cta">
        {cta}
      </button>
    )}
  </div>
);
